/**
 * AVBP Rate Card — the editable services catalogue model + L3B calculator.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS (Quotes RESTORE — Stage 1, 2026-06-14)
 * ─────────────────────────────────────────────────────────────────────────
 *  The previous AVBP version BUILT a services + INR/USD rate-card + quote
 *  builder; it was parked in `src/_legacy/`. This is the STAGE-1 restore:
 *  the editable INR+USD rate card ONLY — the services catalogue (`services/`
 *  collection), an admin UI to view/edit each service (name, tier, unit,
 *  rINR, rUSD, HSN, deliverables, default milestones, firmCompat), and the
 *  L3B scale-pricing calculator. The quote builder + quote→PDF + send
 *  (Stage 2) and the project/money wiring (Stage 3) are LATER dispatches.
 *
 *  The legacy machine (`_legacy/routes/ServicesAdmin.tsx`,
 *  `_legacy/types/quote.ts`, `scripts/seed-quotes.ts`,
 *  `firebase-functions/.../l3bCalculator.js`) is reused; the legacy
 *  CONTENT was stale (generic "Free Vastu Insight Call" names that conflict
 *  with the locked Set-4 tier names, an L1A/L1B deliverable swap). This
 *  module carries the REFRESHED catalogue reconciled from the locked
 *  sources (see REFRESHED_CATALOGUE below) — but because the rate card is
 *  EDITABLE, the admin can correct any field in the UI.
 *
 *  PURE/IMPURE split (mirrors staffRoles.ts / projectStore.ts):
 *    `serviceRowFrom`, `servicePatchBody`, `sortServices`, `formatMoney`,
 *    `calculateL3B`, `REFRESHED_CATALOGUE` are PURE + unit-tested. Only
 *    `listenServices` / `updateService` touch Firestore.
 *
 *  RULES CEILING (NO firestore.rules change needed):
 *    match /services/{svcId} {
 *      allow read:  if isTeam();
 *      allow write: if hasClaim('admin') || isOwnerEmail();
 *    }
 *  This block already exists + is deployed (Quotes Phase-1 foundation,
 *  2026-04-20). An admin session writes `services/{id}` directly — the
 *  Settings → Rate Card ROUTE is additionally admin-gated in App.tsx.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

/* ════════════════════ Types (ported from _legacy/types/quote.ts) ════════════ */

export type ServiceTier =
  | 'L1A'
  | 'L1B'
  | 'L1C'
  | 'L2A'
  | 'L2B'
  | 'L2C'
  | 'L2D'
  | 'L3A'
  | 'L3B'
  | 'CUSTOM';

export type LineUnit =
  | 'project'
  | 'sqft'
  | 'floor'
  | 'hrs'
  | 'days'
  | 'weeks'
  | 'visit'
  | 'set'
  | 'pcs'
  | 'formula'; // L3B → calculator-driven

export type FirmId = 'firm1' | 'firm2';

export interface ServiceMilestone {
  label: string;
  isAdvance: boolean;
  pct: number;
  dueRule: string;
  deliverables?: string;
}

export interface ServiceCatalogEntry {
  svcId: string;
  name: string;
  desc: string;
  tier: ServiceTier;
  unit: LineUnit;
  rINR: number;
  rUSD: number;
  defaultHsn: string;
  firmCompat: FirmId[];
  /** Only true for L3B — rate is ignored, the calculator drives the amount. */
  isFormula?: boolean;
  deliverables?: string;
  defaultMilestones?: ServiceMilestone[];
}

/** The fields the Rate-Card admin can edit (everything else is identity/system). */
export interface ServiceEditableFields {
  name: string;
  desc: string;
  rINR: number;
  rUSD: number;
  defaultHsn: string;
  deliverables: string;
  defaultMilestones: ServiceMilestone[];
}

/* ════════════════════ Due-rule + tier vocab (UI dropdowns) ════════════════ */

export const DUE_RULE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'on_signing', label: 'On signing (advance)' },
  { value: 'before_milestone', label: 'Before milestone release' },
  { value: 'on_milestone', label: 'On milestone' },
  { value: 'on_delivery', label: 'On delivery' },
  { value: 'relative_days', label: 'Relative days' },
  { value: 'fixed_date', label: 'Fixed date' },
];

export const HSN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '998321', label: '998321 — Vastu / architectural advisory' },
  { value: '998344', label: '998344 — Surveying & map-making' },
  { value: '999799', label: '999799 — Other professional / technical' },
];

/* ════════════════════ L3B calculator — 1:1 TS PORT (LOCKED) ════════════════
 *
 *  Mirrors firebase-functions/functions/lib/l3bCalculator.js EXACTLY.
 *  If you edit the math here, mirror it there (or the CF recompute will
 *  silently disagree with the wizard preview — a bug). Constants LOCKED:
 *    ₹80,000 / $560 per 1,000 sqft per floor · min ₹50,000 / $625
 *    bands 100% / 80% / 60% (5k / 10k / ∞ on TOTAL project sqft)
 *    floor multiplier 100% (first) / 80% (additional)
 *    special floors: basement 1.00 · stilt 1.00 · mezzanine 0.50
 */

export const L3B_CONSTANTS = {
  baseRateINR_per1000sqft_per1floor: 80000,
  baseRateUSD_per1000sqft_per1floor: 560,
  minimumQuoteINR: 50000,
  minimumQuoteUSD: 625,
  bands: [
    { upTo: 5000, pct: 1.0 },
    { upTo: 10000, pct: 0.8 },
    { upTo: Infinity, pct: 0.6 },
  ],
  floorMultipliers: {
    first: 1.0,
    additional: 0.8,
  },
  specialFloors: {
    basement: { countsAs: 1.0 },
    stilt: { countsAs: 1.0 },
    mezzanine: { countsAs: 0.5 },
  },
} as const;

export type L3BCurrency = 'INR' | 'USD';

export interface L3BFloorInput {
  label: string;
  sqft: number;
  /** Basement/stilt = 1.0, mezzanine = 0.5; default 1.0 if unset. */
  countsAs?: number;
}

export interface L3BFloorBreakdownRow {
  label: string;
  sqft: number;
  countsAs: number;
  multiplier: number;
}

export interface L3BBandBreakdownRow {
  rangeLabel: string;
  sqft: number;
  ratePct: number;
}

export interface L3BCalcMeta {
  sqft: number;
  floors: number;
  floorBreakdown: L3BFloorBreakdownRow[];
  bandApplied: 'full' | 'tapered' | 'deep';
  bandBreakdown: L3BBandBreakdownRow[];
  minClampApplied: boolean;
  currencyMode: L3BCurrency;
}

export interface L3BCalcResult {
  amount: number;
  calcMeta: L3BCalcMeta;
}

/**
 * L3B scale-price calculator — pure. See §5.3 of AVBP_Quotes_Module_Spec.md.
 * Worked examples (all pass): 3,000 IN → ₹2,40,000 · 7,000 IN → ₹5,28,000 ·
 * 12,000/2F IN → ₹7,34,400 · 500 IN → ₹50,000 (min clamp) · 2,000 INTL → $1,120.
 */
export function calculateL3B(input: {
  floors: L3BFloorInput[];
  currency: L3BCurrency;
}): L3BCalcResult {
  if (!input || !Array.isArray(input.floors) || input.floors.length === 0) {
    throw new Error('calculateL3B: input.floors must be a non-empty array');
  }
  if (input.currency !== 'INR' && input.currency !== 'USD') {
    throw new Error(`calculateL3B: unsupported currency '${input.currency}'`);
  }

  const C = L3B_CONSTANTS;
  const baseRate =
    input.currency === 'INR'
      ? C.baseRateINR_per1000sqft_per1floor
      : C.baseRateUSD_per1000sqft_per1floor;
  const minQuote =
    input.currency === 'INR' ? C.minimumQuoteINR : C.minimumQuoteUSD;

  // Step 1 — total weighted sqft
  const totalSqft = input.floors.reduce(
    (sum, f) =>
      sum + (Number(f.sqft) || 0) * (f.countsAs != null ? Number(f.countsAs) : 1.0),
    0,
  );

  if (totalSqft <= 0) {
    return {
      amount: minQuote,
      calcMeta: {
        sqft: 0,
        floors: input.floors.length,
        floorBreakdown: input.floors.map((f, idx) => ({
          label: f.label,
          sqft: Number(f.sqft) || 0,
          countsAs: f.countsAs != null ? Number(f.countsAs) : 1.0,
          multiplier:
            idx === 0
              ? C.floorMultipliers.first
              : C.floorMultipliers.additional,
        })),
        bandApplied: 'full',
        bandBreakdown: [],
        minClampApplied: true,
        currencyMode: input.currency,
      },
    };
  }

  // Step 2 — blended effective rate via band taper on the TOTAL project sqft.
  let effectiveRate = 0;
  let remaining = totalSqft;
  let consumed = 0;
  const bandBreakdown: L3BBandBreakdownRow[] = [];
  for (const band of C.bands) {
    const bandCapacity = band.upTo - consumed;
    const sliceSqft = Math.min(remaining, bandCapacity);
    if (sliceSqft <= 0) break;
    effectiveRate += (sliceSqft / totalSqft) * baseRate * band.pct;
    bandBreakdown.push({
      rangeLabel: `up to ${band.upTo === Infinity ? '∞' : band.upTo}`,
      sqft: sliceSqft,
      ratePct: band.pct,
    });
    consumed += sliceSqft;
    remaining -= sliceSqft;
    if (remaining <= 0) break;
  }

  // Step 3 — floor multiplier on each floor's weighted sqft
  let gross = 0;
  const floorBreakdown: L3BFloorBreakdownRow[] = [];
  input.floors.forEach((f, idx) => {
    const mult =
      idx === 0 ? C.floorMultipliers.first : C.floorMultipliers.additional;
    const countsAs = f.countsAs != null ? Number(f.countsAs) : 1.0;
    const weighted = (Number(f.sqft) || 0) * countsAs;
    const floorCost = (weighted * effectiveRate * mult) / 1000;
    gross += floorCost;
    floorBreakdown.push({
      label: f.label,
      sqft: Number(f.sqft) || 0,
      countsAs,
      multiplier: mult,
    });
  });

  // Step 4 — minimum clamp
  const minClampApplied = gross < minQuote;
  const total = Math.max(gross, minQuote);

  return {
    amount: Math.round(total),
    calcMeta: {
      sqft: totalSqft,
      floors: input.floors.length,
      floorBreakdown,
      bandApplied:
        totalSqft <= 5000 ? 'full' : totalSqft <= 10000 ? 'tapered' : 'deep',
      bandBreakdown,
      minClampApplied,
      currencyMode: input.currency,
    },
  };
}

/* ════════════════════ Refreshed catalogue (LOCKED-source reconciliation) ════
 *
 *  Reconciled from the locked sources (the legacy seed CONTENT was stale):
 *   • Tier names = Set-4 "Modern + Sanskrit Seal" — VERBATIM from
 *     memory project_tier_naming_set_locked + the LIVE report templates
 *     (l1aTemplate.js "The Selection Path · Disha"; l2bTemplate.js
 *     "The Alignment Path · Nidana"). NEVER generic "Quick/Detailed Review".
 *   • Rates = DELIVERY_COST_MATRIX.md: L1A/L1B ₹11,000 (floor, no discount) ·
 *     L2A/B/C/D ₹60,000 flat · L3A ₹18,000 · L3B FORMULA (calculator).
 *   • L1A/L1B deliverable swap FIXED (legacy had the audit-report blurb on
 *     L1A and the call blurb on L1B — corrected here: L1A = selection-stage
 *     diagnostic + live remedy walkthrough on call; L1B = room-by-room
 *     diagnosis + written prioritised remedy checklist).
 *
 *  ⚠ FLAGGED FOR OWNER SIGN-OFF (rates the matrix does NOT confirm):
 *   • USD figures for L1A/L1B ($135), L2A–D ($750), L3A ($225) are the
 *     LEGACY-SEED convention only — DELIVERY_COST_MATRIX confirms the INR
 *     figures + the L3B USD ($560/min $625) but is SILENT on the L1/L2/L3A
 *     USD rates. Carried as the editable default; owner to confirm in the UI.
 *   • Whether all four L2 tiers (Mandala/Nidana/Parivartan/Nirmana) truly
 *     share one ₹60,000 flat price, OR whether the locked per-tier engine
 *     (L2A ₹30k@1500sqft · L2C ₹40k@1500sqft, per project_l3b_pricing_model
 *     cross-tier extension) should drive L2A/C/D as scale-priced. The matrix
 *     still shows "₹60,000 flat" for all L2 — kept flat here; owner to rule.
 *
 *  Because the rate card is EDITABLE, the admin corrects any field in the UI.
 */

export const REFRESHED_CATALOGUE: ServiceCatalogEntry[] = [
  {
    svcId: 'L1A',
    name: 'The Selection Path · Disha',
    desc:
      'L1A — property-selection diagnostic for a buyer weighing a property ' +
      'before commitment. Vastu zoning + Astro-Vastu rashi-direction read ' +
      'against the stated requirement.',
    tier: 'L1A',
    unit: 'project',
    rINR: 11000,
    rUSD: 135,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Selection-stage Vastu report: entrance/kitchen/bedroom/toilet zoning, ' +
      'family rashi-direction matrix, and a buy / negotiate / avoid read. ' +
      'Specific remedies walked through live on the consultation call.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 100,
        dueRule: 'on_signing',
        deliverables: 'Selection-stage report + live consultation call.',
      },
    ],
  },
  {
    svcId: 'L1B',
    name: 'The Clarity Path · Darshan',
    desc:
      'L1B — room-by-room diagnosis of an owned/occupied property with a ' +
      'prioritised written remedy checklist.',
    tier: 'L1B',
    unit: 'project',
    rINR: 11000,
    rUSD: 135,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Written room-by-room Vastu diagnosis + prioritised remedy checklist ' +
      '(placement, material, colour and metal per flagged item).',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 100,
        dueRule: 'on_signing',
        deliverables: 'Room-by-room diagnosis + written remedy checklist.',
      },
    ],
  },
  {
    // L1C (2026-06-21 spec) — "The Review Path · Sameeksha". A ₹11,000 / $135
    // report-only PLAN REVIEW of an architect's drawing set. Single line, single
    // 100% advance (L1 band) — money-before-delivery. Add-on "+₹4,000 per extra
    // plan (max 3)" is a MANUAL line item the staffer adds, not a catalogue tier.
    // L1C credits 100% toward L2A/L2B/L2C/L2D within 60 days (the Setu credit).
    svcId: 'L1C',
    name: 'The Review Path · Sameeksha',
    desc:
      'L1C — independent Vastu review of an architect’s plan set: 16-zone + ' +
      '32-entrance verdict, compliance scorecard and a prioritised fix-list. ' +
      'Report-only — no redline drawings, no architect coordination.',
    tier: 'L1C',
    unit: 'project',
    rINR: 11000,
    rUSD: 135,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Condensed plan-review verdict report — 16-zone bar chart, 32-entrance ' +
      'verdict, Vastu Compliance Scorecard + prioritised fix-list (Critical/' +
      'Major/Minor) + client-facing video.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 100,
        dueRule: 'on_signing',
        deliverables: 'Plan-review verdict report on payment (money-before-delivery).',
      },
    ],
  },
  {
    svcId: 'L2A',
    name: 'The Layout Path · Mandala',
    desc:
      'L2A — conceptual Vastu layout for a raw plot / bare-shell floor plate ' +
      '(residence · office · commercial). Zone allocation before build-out.',
    tier: 'L2A',
    unit: 'project',
    rINR: 60000,
    rUSD: 750,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Conceptual Vastu layout: 16-zone activity allocation, entrance + ' +
      'room placement guidance, annotated concept plan.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 50,
        dueRule: 'on_signing',
        deliverables: 'Engagement confirmed.',
      },
      {
        label: 'Before layout document release',
        isAdvance: false,
        pct: 50,
        dueRule: 'before_milestone',
        deliverables: 'Conceptual Vastu layout + annotated concept plan.',
      },
    ],
  },
  {
    svcId: 'L2B',
    name: 'The Alignment Path · Nidana',
    desc:
      'L2B — deep-diagnosis review of an occupied property: per-floor ' +
      'entrance (32-grid) + activities/utilities/objects (16-grid) + ' +
      'Panchatattva, full per-item walkthrough, Day-21 + Day-44 reviews.',
    tier: 'L2B',
    unit: 'project',
    rINR: 60000,
    rUSD: 750,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Detailed Vastu review document + per-item remedy walkthrough across ' +
      'all floors, with a Day-21 quick check-in and Day-44 implementation review.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 50,
        dueRule: 'on_signing',
        deliverables: 'Engagement confirmed.',
      },
      {
        label: 'Before review document release',
        isAdvance: false,
        pct: 50,
        dueRule: 'before_milestone',
        deliverables:
          'Detailed Vastu review document + annotated per-item remedies.',
      },
    ],
  },
  {
    svcId: 'L2C',
    // Label PATCHED 2026-06-21 → "Integration Path · Samanvaya". The svcId / tier
    // KEY stays `L2C` everywhere (Firestore, quote.tier, report map, analytics);
    // only the human label flips. Renaming the key would orphan existing L2C data.
    name: 'The Integration Path · Samanvaya',
    desc:
      'L2C — architect coordination & integration: redline the architect’s ' +
      'drawings and align both professionals on the fixes (joint call, sign-off).',
    tier: 'L2C',
    unit: 'project',
    rINR: 60000,
    rUSD: 750,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Change/re-layout assessment report + prioritised re-zoning ' +
      'recommendations.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 50,
        dueRule: 'on_signing',
        deliverables: 'Engagement confirmed.',
      },
      {
        label: 'Before assessment report release',
        isAdvance: false,
        pct: 50,
        dueRule: 'before_milestone',
        deliverables: 'Change/re-layout assessment + recommendations.',
      },
    ],
  },
  {
    svcId: 'L2D',
    // AD-042 rename, owner-ratified one-tap 2026-07-03: L2D = "The Detailing
    // Path · Vistara" (data key L2D unchanged; "Nirmana" stays RESERVED for
    // the future L4A build tier). This seed had lagged the rename that
    // intakeCatalog already carried.
    name: 'The Detailing Path · Vistara',
    desc:
      'L2D — build-stage specialised Vastu consultation (under-construction ' +
      'guidance, structural-stage directions, specialised cases).',
    tier: 'L2D',
    unit: 'project',
    rINR: 60000,
    rUSD: 750,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Build-stage specialised Vastu report tailored to the construction / ' +
      'consult scope.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 50,
        dueRule: 'on_signing',
        deliverables: 'Engagement confirmed.',
      },
      {
        label: 'Before specialised report release',
        isAdvance: false,
        pct: 50,
        dueRule: 'before_milestone',
        deliverables: 'Build-stage specialised Vastu report.',
      },
    ],
  },
  {
    svcId: 'L3A',
    name: 'The Brief Path · Sankalpa',
    desc:
      'L3A — standalone Vastu design brief: activity/zone mapping + ' +
      'room/zone suitability per family member or business partner, plus a ' +
      'few key drawings. Fixed report.',
    tier: 'L3A',
    unit: 'project',
    rINR: 18000,
    rUSD: 225,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    deliverables:
      'Vastu design brief: activity/zone mapping, room/zone suitability per ' +
      'family member or partner, and a few key reference drawings.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 50,
        dueRule: 'on_signing',
        deliverables: 'Engagement confirmed + site brief shared.',
      },
      {
        label: 'Before design brief release',
        isAdvance: false,
        pct: 50,
        dueRule: 'before_milestone',
        deliverables: 'Vastu design brief + reference drawings.',
      },
    ],
  },
  {
    svcId: 'L3B',
    name: 'The Design Path · Sthapana',
    desc:
      'L3B — full Vastu-based design & single-point delivery, scale-priced ' +
      'via the L3B calculator. ₹80k / 1,000 sqft / floor with band taper ' +
      '(100% / 80% / 60%) and floor multiplier (100% / 80%). ₹50,000 / $625 ' +
      'minimum. Basement 100%, stilt 100%, mezzanine 50%.',
    tier: 'L3B',
    unit: 'formula',
    rINR: 0,
    rUSD: 0,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
    isFormula: true,
    deliverables:
      'Full Vastu design package per floor: zone assignment, room placement, ' +
      'door/window/staircase directions, material recommendations, and a ' +
      'full implementation guide.',
    defaultMilestones: [
      {
        label: 'Advance',
        isAdvance: true,
        pct: 30,
        dueRule: 'on_signing',
        deliverables: 'Engagement confirmed + site brief shared.',
      },
      {
        label: 'Before preliminary design + zone layout (per floor) release',
        isAdvance: false,
        pct: 40,
        dueRule: 'before_milestone',
        deliverables: 'Preliminary design + zone layout per floor delivered.',
      },
      {
        label: 'Before full design package release',
        isAdvance: false,
        pct: 30,
        dueRule: 'before_milestone',
        deliverables:
          'Full Vastu design package per floor (zones, placement, directions, ' +
          'materials, implementation guide).',
      },
    ],
  },
  {
    svcId: 'ADDON_SITE_VISIT',
    name: 'Site-visit add-on',
    desc:
      'On-site consultant visit. Per-visit. Travel billed at actuals unless ' +
      'otherwise agreed.',
    tier: 'CUSTOM',
    unit: 'visit',
    rINR: 0,
    rUSD: 0,
    defaultHsn: '998321',
    firmCompat: ['firm1', 'firm2'],
  },
  {
    svcId: 'ADDON_MAP_MAKING',
    name: 'Map-making / survey add-on',
    desc: 'Surveying and map-making for sites lacking dimensioned drawings.',
    tier: 'CUSTOM',
    unit: 'project',
    rINR: 0,
    rUSD: 0,
    defaultHsn: '998344',
    firmCompat: ['firm1', 'firm2'],
  },
  {
    svcId: 'CUSTOM_GEN_CONSULT',
    name: 'Generic consulting fallback',
    desc: 'Catch-all for custom lines that do not fit the L1/L2/L3 ladder.',
    tier: 'CUSTOM',
    unit: 'project',
    rINR: 0,
    rUSD: 0,
    defaultHsn: '999799',
    firmCompat: ['firm1', 'firm2'],
  },
];

/* ════════════════════ Tier ordering ════════════════════ */

const TIER_ORDER: ServiceTier[] = [
  'L1A',
  'L1B',
  // Vera MEDIUM-1 (2026-07-04): L1C was in the ServiceTier union + the
  // new-service select but MISSING here — serviceCreateBody's tier fallback
  // silently rewrote an L1C pick to CUSTOM, and sortServices indexed it -1.
  'L1C',
  'L2A',
  'L2B',
  'L2C',
  'L2D',
  'L3A',
  'L3B',
  'CUSTOM',
];

/** PURE — stable rate-card display order: by tier ladder, then svcId. */
export function sortServices(list: ServiceCatalogEntry[]): ServiceCatalogEntry[] {
  return [...list].sort((a, b) => {
    const ta = TIER_ORDER.indexOf(a.tier);
    const tb = TIER_ORDER.indexOf(b.tier);
    if (ta !== tb) return ta - tb;
    return a.svcId < b.svcId ? -1 : a.svcId > b.svcId ? 1 : 0;
  });
}

/* ════════════════════ Money formatting ════════════════════ */

/** PURE — INR with Indian digit grouping. */
export function formatINR(n: number): string {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

/** PURE — USD plain. */
export function formatUSD(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/* ════════════════════ Row + patch model (PURE) ════════════════════ */

/**
 * PURE — map a raw `services/{id}` doc to a typed ServiceCatalogEntry,
 * defensively coercing every field (Firestore returns `unknown`). Unknown
 * tiers/units fall back to safe defaults so the screen never crashes on a
 * partial doc; numbers coerce to 0; arrays coerce to [].
 */
export function serviceRowFrom(
  svcId: string,
  d: Record<string, unknown>,
): ServiceCatalogEntry {
  const tier = TIER_ORDER.includes(d.tier as ServiceTier)
    ? (d.tier as ServiceTier)
    : 'CUSTOM';
  const milestones = Array.isArray(d.defaultMilestones)
    ? (d.defaultMilestones as unknown[]).map((m) => {
        const mm = (m ?? {}) as Record<string, unknown>;
        return {
          label: String(mm.label ?? 'Milestone'),
          isAdvance: mm.isAdvance === true,
          pct: Number(mm.pct) || 0,
          dueRule: String(mm.dueRule ?? 'on_milestone'),
          deliverables:
            mm.deliverables != null ? String(mm.deliverables) : undefined,
        } as ServiceMilestone;
      })
    : undefined;
  const firmCompat = Array.isArray(d.firmCompat)
    ? (d.firmCompat as unknown[])
        .map((f) => String(f))
        .filter((f): f is FirmId => f === 'firm1' || f === 'firm2')
    : [];
  return {
    svcId,
    name: String(d.name ?? svcId),
    desc: String(d.desc ?? ''),
    tier,
    unit: String(d.unit ?? 'project') as LineUnit,
    rINR: Number(d.rINR) || 0,
    rUSD: Number(d.rUSD) || 0,
    defaultHsn: String(d.defaultHsn ?? '998321'),
    firmCompat,
    isFormula: d.isFormula === true,
    deliverables: d.deliverables != null ? String(d.deliverables) : undefined,
    defaultMilestones: milestones,
  };
}

/**
 * PURE — validate + normalise the editable fields into the EXACT doc body an
 * updateService write sends. Only the editable fields + an audit stamp are
 * ever written (svcId / tier / unit / firmCompat / isFormula stay untouched,
 * so the write can never corrupt the service's identity). Throws on invalid
 * input so a bad write can never reach Firestore.
 */
export function servicePatchBody(
  fields: ServiceEditableFields,
  actorName: string,
): Record<string, unknown> {
  const name = fields.name.trim();
  if (!name) throw new Error('Service name cannot be empty.');
  const rINR = Number(fields.rINR);
  const rUSD = Number(fields.rUSD);
  if (!Number.isFinite(rINR) || rINR < 0) {
    throw new Error('INR rate must be a non-negative number.');
  }
  if (!Number.isFinite(rUSD) || rUSD < 0) {
    throw new Error('USD rate must be a non-negative number.');
  }
  const hsn = fields.defaultHsn.trim();
  if (!/^\d{4,8}$/.test(hsn)) {
    throw new Error('HSN must be 4–8 digits.');
  }
  const milestones = fields.defaultMilestones.map((m) => {
    const pct = Number(m.pct);
    return {
      label: String(m.label ?? '').trim() || 'Milestone',
      isAdvance: m.isAdvance === true,
      pct: Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0,
      dueRule: String(m.dueRule ?? 'on_milestone'),
      deliverables: String(m.deliverables ?? '').trim(),
    };
  });
  return {
    name,
    desc: fields.desc.trim(),
    rINR: Math.round(rINR),
    rUSD: Math.round(rUSD),
    defaultHsn: hsn,
    deliverables: fields.deliverables.trim(),
    defaultMilestones: milestones,
    rateCardUpdatedBy: actorName,
  };
}

/** PURE — pull the editable subset out of a service for the edit form. */
export function editableFrom(svc: ServiceCatalogEntry): ServiceEditableFields {
  return {
    name: svc.name,
    desc: svc.desc,
    rINR: svc.rINR,
    rUSD: svc.rUSD,
    defaultHsn: svc.defaultHsn,
    deliverables: svc.deliverables ?? '',
    defaultMilestones: (svc.defaultMilestones ?? []).map((m) => ({ ...m })),
  };
}

/* ════════════════════ Firestore seam (impure) ════════════════════ */

/**
 * Live realtime list of the `services/` catalogue. `read: if isTeam()`, so
 * any signed-in team member can read — but the Settings → Rate Card ROUTE is
 * admin-gated in App.tsx, so only an admin reaches this screen.
 */
export function listenServices(
  cb: (rows: ServiceCatalogEntry[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'services')),
    (snap) => {
      const rows: ServiceCatalogEntry[] = [];
      for (const dd of snap.docs) rows.push(serviceRowFrom(dd.id, dd.data()));
      cb(sortServices(rows));
    },
    (e) => onErr?.(e),
  );
}

/**
 * Write the edited fields to `services/{svcId}`. Rules: `write: if
 * hasClaim('admin') || isOwnerEmail()` — an admin session satisfies this, so
 * NO rules change is needed. The write is intentionally MINIMAL (editable
 * fields + audit stamp); identity fields (svcId/tier/unit/firmCompat/
 * isFormula) are never touched.
 */
export async function updateService(
  svcId: string,
  fields: ServiceEditableFields,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'services', svcId), {
    ...servicePatchBody(fields, actorName),
    rateCardUpdatedAt: serverTimestamp(),
  });
}

/* ════════════════════ Create a NEW service (Phase 3d, 2026-07-03) ═══════════
 *
 * Admin mints a new catalogue entry — code, Set-4-style display name,
 * standard prices, milestones — through the SAME `services/{id}` store the
 * editor writes (rules: the same admin `write` gate covers a create; NO
 * rules change). NAMING CANON stays intact: a new service supplies its own
 * client-facing name; the locked Set-4 tier names in REFRESHED_CATALOGUE are
 * never touched, and `canonicalServiceNameFor` resolves by svcId/tier as
 * before (live rows override static via `mergeCatalogue` at read sites).
 *
 * SCOPE (honest): a NEW service is a QUOTE-LINE service (the quote line
 * picker prices anything). It does NOT become a lead `assignedTier` — the
 * tier set is the frozen engagementTemplate workflow config whose money-gate
 * mirror also lives in the functions repo (gate-mirror lockstep): adding a
 * tier requires BOTH repos and is deliberately out of this create flow.
 */

/** PURE — validate + normalise the identity + editable fields into the EXACT
 *  doc body a createService write sends. Throws on invalid input. */
export function serviceCreateBody(
  input: {
    svcId: string;
    tier: ServiceTier;
    unit?: LineUnit;
    fields: ServiceEditableFields;
  },
  actorName: string,
): { svcId: string; body: Record<string, unknown> } {
  const svcId = input.svcId.trim().toUpperCase();
  if (!/^[A-Z0-9][A-Z0-9-]{1,11}$/.test(svcId)) {
    throw new Error(
      'Service code must be 2–12 characters — letters/digits/dashes (e.g. GEO-SCAN).',
    );
  }
  const tier: ServiceTier = TIER_ORDER.includes(input.tier) ? input.tier : 'CUSTOM';
  return {
    svcId,
    body: {
      ...servicePatchBody(input.fields, actorName),
      tier,
      unit: String(input.unit ?? 'project'),
      // both firms can bill it by default (the quote routes the firm anyway).
      firmCompat: ['firm1', 'firm2'],
      isFormula: false,
    },
  };
}

/** Create `services/{code}` (refuses to overwrite an existing service). */
export async function createService(
  input: {
    svcId: string;
    tier: ServiceTier;
    unit?: LineUnit;
    fields: ServiceEditableFields;
  },
  actorName: string,
  /** the currently-listed services — the duplicate-code guard. */
  existing: ServiceCatalogEntry[],
): Promise<string> {
  const { svcId, body } = serviceCreateBody(input, actorName);
  if (existing.some((s) => s.svcId === svcId)) {
    throw new Error(`Service code ${svcId} already exists — pick another code.`);
  }
  await setDoc(doc(db, 'services', svcId), {
    ...body,
    rateCardUpdatedAt: serverTimestamp(),
  });
  return svcId;
}

/**
 * PURE — the catalogue every picker reads: the LIVE `services/` rows merged
 * OVER the static REFRESHED_CATALOGUE (live wins by svcId; static rows the
 * live set doesn't carry stay — so pickers keep working before the live list
 * loads AND a brand-new service appears the moment its doc lands). Sorted by
 * the same tier order as the rate card.
 */
export function mergeCatalogue(
  live: ServiceCatalogEntry[] | null | undefined,
  staticList: ServiceCatalogEntry[] = REFRESHED_CATALOGUE,
): ServiceCatalogEntry[] {
  const liveRows = Array.isArray(live) ? live : [];
  const liveIds = new Set(liveRows.map((s) => s.svcId));
  return sortServices([
    ...liveRows,
    ...staticList.filter((s) => !liveIds.has(s.svcId)),
  ]);
}
